import request from '@/requests'
import { BaseScreenManager } from '@/logic/common'
import { actions } from '@/store'
import { Address } from '@/interfaces'
import { notifications } from '@/util'
import VueRouter from 'vue-router'
import Action, { ExistingAction } from './CallCentreAction'
import Dashboard from './CallCentreDashboard'

interface Tasks {
  design: object
  installation: {
    tickets: Array<any>
  }
  inspection: object
}

export interface ClientProject {
  id: number | null
  name: string
  status: string | null
  product_type: string | null
}

export interface ProjectTaskSummary {
  status: string
  status_at: string
  appointment_date: string | null
}

interface Client {
  client: object
  address: Address
  woco: string
  project: string
  project_id?: number
  projects?: Array<ClientProject>
  tasks_by_project?: {
    [projectName: string]: {
      project_id: number
      tasks: { [taskType: string]: ProjectTaskSummary }
    }
  }
  groups: Array<string>
  group_projects?: { [group: string]: string }
  tasks: Tasks
  fullAddress: string
  out_of_service?: string | null
}

export default class CallCentreScreenManager extends BaseScreenManager {
  public isAddressesLoading = false
  public isAddressLoading = false
  public isActionsLoading = false
  public isEleenaSyncing = false
  public isMarkingOutOfService = false

  public isInvalidAddress = false

  public isActive = false
  public query = ''

  public selectedAddress: null | Client = null
  public fullAddress = ''
  public selectedStatus = 'Client'
  public selectedProjectId: number | null = null
  public ktoSurveyUrl: null | string = null

  public actions: Array<Action> = []
  public addresses: Array<string> = []

  public selectedAction: Action | null = null
  public actionWasSelected = false

  public action = new Action()
  public dashboard = new Dashboard()

  private timer: null | number = null
  private minCharactersForQuery = 2

  constructor(router: VueRouter) {
    super(router)
    const queryAddress = router.currentRoute.query.address
    const address =
      (typeof queryAddress === 'string' && queryAddress) ||
      actions.common.readSelectedCallCentreAddress()
    if (address) this.setSelectedAddress(address)
  }

  get possibleStatuses() {
    if (!this.selectedAddress!.tasks) return ['Client']
    const statuses = [
      'Client',
      'Eleena',
      ...Object.keys(this.selectedAddress!.tasks)
        .filter((status) => !['maintenance', 'finance'].includes(status.toLowerCase()))
        .map((status) => status.charAt(0).toUpperCase() + status.slice(1)),
    ]

    return statuses
  }

  public formatDate = (date: Date, isUTC = false) => {
    return date.toLocaleString('en-GB', {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: isUTC ? 'UTC' : undefined,
    })
  }

  public setSelectedAction = (action: Action) => {
    this.actionWasSelected = true
    this.selectedAction = action
  }

  public setQuery = (event: InputEvent) => {
    const { value } = event.target as HTMLInputElement
    this.query = value
    if (this.query.length < this.minCharactersForQuery) {
      this.addresses = []
      return
    }

    if (this.timer) clearTimeout(this.timer)

    this.timer = setTimeout(() => {
      this.setAddresses()
    }, 300)
  }

  public setAddresses = async () => {
    this.isAddressesLoading = true
    this.isActive = true

    if (this.query.length < this.minCharactersForQuery) {
      this.addresses = []
    } else {
      const response = await request.clients.getClientsForQuery(this.query)
      if (response.data) {
        this.addresses = response.data.map((address: any) => address.household.address)
      } else {
        this.addresses = []
      }
    }

    this.isAddressesLoading = false
  }

  public setSelectedAddress = async (value: string) => {
    this.fullAddress = value
    this.addresses = []
    this.query = value
    this.isActive = false
    this.isInvalidAddress = false
    this.selectedStatus = 'Client'
    this.selectedProjectId = null
    this.ktoSurveyUrl = null

    actions.common.commitSelectedCallCentreAddress(value)

    if (this.router.currentRoute.query.address !== value) {
      this.router.replace({ query: { address: value } }).catch(() => undefined)
    }

    const loaded = await this.fetchClientData(value)
    if (loaded) {
      // Data-service picked the project and already scoped the data to it
      this.selectedProjectId = this.selectedAddress!.project_id ?? null

      this.setActions(value)
      this.setKtoSurveyUrl(value, this.selectedAddress!, this.selectedProject)
      this.action.address = value
      this.action.woco = this.selectedAddress!.woco
    }
  }

  get clientProjects(): Array<ClientProject> {
    return (this.selectedAddress && this.selectedAddress.projects) || []
  }

  get selectedProject(): ClientProject | null {
    return this.clientProjects.find((p) => p.id === this.selectedProjectId) || null
  }

  public isNolyProject = (project: ClientProject) =>
    project.product_type === 'noly' || /noly/i.test(project.name || '')

  public setSelectedProject = async (projectId: number | null) => {
    if (this.selectedProjectId === projectId) return
    this.selectedProjectId = projectId
    // The actions (comments) stay address-level; only the task data refetches
    if (this.fullAddress) {
      const loaded = await this.fetchClientData(this.fullAddress)
      // The survey is per project (a PV and a Noly project on one address
      // have different forms), so the link follows the selected project
      if (loaded) {
        this.ktoSurveyUrl = null
        this.setKtoSurveyUrl(this.fullAddress, this.selectedAddress!, this.selectedProject)
      }
    }
  }

  private fetchClientData = async (value: string): Promise<boolean> => {
    this.isAddressLoading = true
    const requestedProjectId = this.selectedProjectId
    const response = await request.data.getDataForClient(value, requestedProjectId)

    // A slow response for a previously selected address or project must not
    // overwrite what is on screen now (same guard as setKtoSurveyUrl); the
    // newer selection's own fetch resets isAddressLoading
    if (this.fullAddress !== value || this.selectedProjectId !== requestedProjectId) {
      return false
    }

    let loaded = false
    if (response.data && response.data.client) {
      response.data.fullAddress = this.fullAddress
      this.selectedAddress = response.data
      loaded = true
    } else {
      this.isInvalidAddress = true
    }
    this.isAddressLoading = false
    return loaded
  }

  public setKtoSurveyUrl = async (
    fullAddress: string,
    client: Client,
    project: ClientProject | null
  ) => {
    if (!client.address) return

    // The KTO service matches survey responses on an exact string compare of
    // "street house_number postal_code", the format it puts in the mail links.
    // That is not the same as fullAddress, which has no space in the postcode.
    const { street, house_number, postal_code } = client.address
    const ktoAddress = `${street} ${house_number} ${postal_code}`

    // The KTO service decides between the regular and the Noly form on the
    // product type, with the project name as fallback (ITR-1802). Its own
    // lookup of the project by address string fails on the spaced postcode
    // above, so tell it what we already know about the project.
    const productType = project ? project.product_type : null
    const projectName = (project && project.name) || client.project || null
    const requestedProjectId = this.selectedProjectId

    const response = await request.kto.getSurveyUrl(
      client.woco,
      ktoAddress,
      productType,
      projectName
    )

    // A slow response for a previously selected address or project must not
    // end up on what is on screen now, that would open a survey for the wrong one
    if (this.fullAddress !== fullAddress || this.selectedProjectId !== requestedProjectId) return

    this.ktoSurveyUrl = (response.data && response.data.survey_url) || null
  }

  public setActions = async (address: string) => {
    this.isActionsLoading = true

    const response = await request.actions.getActionsForAddress(address)

    if (response.data && response.data.actions) {
      this.actions = response.data.actions.map((action: ExistingAction) => new Action(action))
    }
    this.isActionsLoading = false
  }

  public setSelectedStatus = (status: string) => {
    this.selectedStatus = status
  }

  public syncEleena = async () => {
    if (this.selectedAddress) {
      this.isEleenaSyncing = true
      const response = await request.eleena.syncData(this.selectedAddress)
      this.isEleenaSyncing = false
      if (response.status_code === 200) {
        notifications.addNotification({
          message: 'Eleena gesynchroniseerd',
          type: 'success',
        })

        const responseData = response.data
        if (
          responseData !== undefined &&
          responseData.login_details &&
          responseData.login_details.username &&
          responseData.login_details.password
        ) {
          alert(
            `De bewoner kan nu inloggen met gebruikersnaam ${responseData.login_details.username} en wachtwoord ${responseData.login_details.password}. Via Monitoring -> 'Send credentials' kan de bewoner ook zelf een wachtwoord instellen.`
          )
        }
      } else {
        notifications.addNotification({
          message: response.message,
          type: 'danger',
        })
      }
    }
  }

  public toggleServiceStatus = async (date: string | null) => {
    if (!this.selectedAddress) return

    this.isMarkingOutOfService = true
    try {
      const response = await request.clients.setServiceStatus(this.fullAddress, date)

      if (response.status_code === 200) {
        this.selectedAddress.out_of_service = date

        await this.logServiceStatusChange(!!date)

        notifications.addNotification({
          message: 'Servicestatus bijgewerkt',
          type: 'success',
        })
      } else {
        notifications.addNotification({
          message: response.message || 'Failed to update service status',
          type: 'danger',
        })
      }
    } catch (error) {
      notifications.addNotification({
        message: `Failed to update service status: ${error}`,
        type: 'danger',
      })
    } finally {
      this.isMarkingOutOfService = false
    }
  }

  private logServiceStatusChange = async (markOutOfService: boolean) => {
    if (!this.selectedAddress) return

    const logAction = new Action()
    logAction.address = this.fullAddress
    logAction.woco = this.selectedAddress.woco
    logAction.type = 'Comment'
    logAction.context = 'Internal'
    logAction.subject = 'Algemeen/proces'
    logAction.is_inbound = true
    logAction.value = markOutOfService ? 'Adres uit dienst gezet' : 'Adres terug in dienst gezet'

    await logAction.create()
    await this.setActions(this.fullAddress)
  }
}
